import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Link2, RefreshCw } from 'lucide-react';
import { useActiveEvent } from '../contexts/EventContext';
import { useAppSelector } from '../store';
import { API_BASE_URL } from '../lib/utils';
import EventDisclosureReportView, {
  downloadDisclosureJson,
  printDisclosureReport,
} from '../components/command/EventDisclosureReportView';
import EventDisclosureShareModal from '../components/command/EventDisclosureShareModal';
import type { EventDisclosureReport } from '../types/disclosure';

function disclosureFetchError(status: number, bodyError?: string): string {
  if (status === 404) {
    return 'Disclosure API not found — the server backend needs updating (deploy index.js and disclosure.js, then restart pm2).';
  }
  if (status === 403) return bodyError || 'Command access required to view disclosure reports.';
  if (status === 401) return 'Session expired — please log in again.';
  return bodyError || `Failed to load disclosure report (HTTP ${status})`;
}

export default function EventDisclosurePage() {
  const { activeEvent, activeEventId } = useActiveEvent();
  const { token } = useAppSelector((s) => s.auth);
  const [report, setReport] = useState<EventDisclosureReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);

  const eventId = activeEvent?.id || activeEventId;

  const loadReport = async () => {
    if (!token || !eventId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/events/${eventId}/disclosure`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(disclosureFetchError(res.status, data.error));
      setReport(data.disclosure);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load disclosure report';
      setError(msg.includes('fetch') || msg === 'Failed to fetch'
        ? 'Could not reach the API — check your connection or backend server.'
        : msg);
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, [token, eventId]);

  if (!eventId) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        No event selected. Open an event from Command Home first.
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-background">
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border px-4 py-3 flex items-center gap-3 no-print">
        <Link
          to={`/workspace/incidents?eventId=${encodeURIComponent(eventId)}`}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to workspace
        </Link>
        <span className="text-xs font-mono text-muted-foreground">{eventId}</span>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={loadReport}
            disabled={loading}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border border-border hover:bg-accent disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setShowShareModal(true)}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Link2 className="w-3 h-3" />
            Share link
          </button>
        </div>
      </div>

      <div className="p-6">
        {loading && (
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <RefreshCw className="w-4 h-4 animate-spin" />
            Building disclosure report…
          </p>
        )}
        {error && (
          <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-4 py-3">
            {error}
          </div>
        )}
        {report && !loading && (
          <EventDisclosureReportView
            report={report}
            onPrint={printDisclosureReport}
            onDownload={() => downloadDisclosureJson(report)}
          />
        )}
      </div>

      {eventId && (
        <EventDisclosureShareModal
          open={showShareModal}
          eventId={eventId}
          eventName={activeEvent?.name || eventId}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  );
}
